import { HookhouseFormData } from '@/types';
import type { PersonaOverride } from '@/types/persona.types';
import { STYLE_PRESETS } from '@/constants';
import { renderLockedStyleTemplate } from '@/shared/personaLock';

export interface PromptPreviewSection {
  label: string;
  content: string;
}

export interface PromptPreviewData {
  userPrompt: string;
  sections: PromptPreviewSection[];
  charCount: number;
}

export function assemblePromptPreview(
  data: HookhouseFormData,
  vocalArchitecture?: string,
  productionArchitecture?: string,
  riffDnaArchitecture?: string,
  soloArchitecture?: string,
  narrativeArchitecture?: string,
  personaOverride?: PersonaOverride | null
): PromptPreviewData {
  const isPersonaActive = !!personaOverride;
  const isPersonaLocked = isPersonaActive && personaOverride!.useLockedTemplate && !!personaOverride!.styleTemplate.trim();
  const isPresetOverride = !isPersonaActive && data.stylePreset && data.stylePreset !== 'None';

  let styleInstruction = `**Musical Blend:** ${data.blends.join(", ")}`;
  if (isPersonaLocked) {
    // See llmShared.ts's buildSunoPromptInput (the real prompt builder --
    // this function is preview-only) for why this branch exists at all.
    styleInstruction = `**LOCKED PERSONA STYLE PROMPT (TOTAL OVERRIDE — reproduce exactly, tempo/key words aside; do not alter, paraphrase, or invent a different sonic direction):**\n${renderLockedStyleTemplate(personaOverride!)}`;
  } else if (isPresetOverride) {
    const presetDescription = STYLE_PRESETS[data.stylePreset];
    if (presetDescription) {
      styleInstruction = `**Style Preset (TOTAL OVERRIDE):**\n${presetDescription}\n\nSTYLE/TAGS RULES (NON-NEGOTIABLE):\n1. The style/tags output MUST derive EXCLUSIVELY from the Style Preset above — genre, sub-genre, instruments, timbres, voice, key, BPM, and production descriptors.\n2. IGNORE all timbral and genre descriptors from any Riff DNA, MOOD, THEMES, INSTRUMENTS, or CONSTRAINTS fields below. Those fields drive LYRICS and NARRATIVE only — they MUST NOT contribute words to the style/tags output.\n3. If Riff DNA below describes heavy/distorted/doom/metal/etc. timbres that conflict with the preset, RE-SKIN the riff's rhythmic and structural intent through the preset's sonic palette. Keep the rhythm pattern; drop the conflicting timbre words.\n4. Do NOT add instruments to the style/tags that are not named in the preset. Do NOT invent extra production gear (preamps, mics, reverbs, etc.) beyond what the preset specifies.`;
    }
  }

  let vocalPackInstruction = "";
  let productionInstruction = "";
  let bandIdentityInstruction = "";

  if (data.isInstrumental) {
    vocalPackInstruction = "\n**INSTRUMENTAL MODE:** No vocals. Pure instrumental composition. Do not generate any vocal lines, lyrics, choir, or sung content. All melodic and emotional content carried by instruments.";
    productionInstruction = !isPresetOverride && productionArchitecture ? `\n**Production Architecture & Multi-Layer Physics Filter:**\n${productionArchitecture}` : "";
  } else if (isPersonaActive) {
    const vocalParts: string[] = [];
    if (personaOverride!.vocalArchitecture) vocalParts.push(`**Vocal Architecture (Persona):**\n${personaOverride!.vocalArchitecture}`);
    if (personaOverride!.vocalPhysicsLocked) vocalParts.push(`**Vocal Physics (Persona — MUST NOT CHANGE, reproduce this delivery exactly):**\n${personaOverride!.vocalPhysicsLocked}`);
    vocalPackInstruction = vocalParts.length ? `\n${vocalParts.join('\n\n')}` : "";

    const prodParts: string[] = [];
    if (personaOverride!.productionArchitecture) prodParts.push(`**Production Architecture (Persona):**\n${personaOverride!.productionArchitecture}`);
    if (personaOverride!.rhythmPhysicsGuidance) prodParts.push(`**Rhythm Section Physics (Persona — changeable within the genre, do not contradict):**\n${personaOverride!.rhythmPhysicsGuidance}`);
    productionInstruction = prodParts.length ? `\n${prodParts.join('\n\n')}` : "";

    bandIdentityInstruction = personaOverride!.bandIdentityBlock ? `\n${personaOverride!.bandIdentityBlock}\n` : "";
  } else {
    vocalPackInstruction = !isPresetOverride && vocalArchitecture ? `\n**Vocal Architecture:**\n${vocalArchitecture}` : "";
    productionInstruction = !isPresetOverride && productionArchitecture ? `\n**Production Architecture & Multi-Layer Physics Filter:**\n${productionArchitecture}` : "";
  }

  let riffDnaInstruction = "";
  if (riffDnaArchitecture) {
    const riffPresetGuard = isPresetOverride
      ? `\n[STYLE PRESET ACTIVE — the riff guidance below provides RHYTHM & STRUCTURE only. Recast all timbral/genre descriptors through the preset's sonic palette. Do NOT let riff timbre words (heavy, distorted, doom, chugging, crushing, etc.) leak into the style/tags output.]\n`
      : "";
    riffDnaInstruction = `\n${riffPresetGuard}${riffDnaArchitecture}\n`;
  }

  let soloInstruction = "";
  if (soloArchitecture) {
    soloInstruction = `\n[SOLO CHOREOGRAPHY INSTRUCTIONS]\nThe song structure includes a solo section. When you reach the solo, use EXACTLY this header and choreography:\n\n${soloArchitecture}\n\nDo NOT improvise or change the solo choreography. Use it exactly as provided above. The solo should appear after the Bridge section in the song structure.\n[END SOLO INSTRUCTIONS]`;
  }

  let narrativeInstruction = "";
  if (narrativeArchitecture) {
    narrativeInstruction = `\n${narrativeArchitecture}\n`;
  }

  const userPrompt = `ARCHITECT SONG BLUEPRINT
SEED: "${data.seed}"
${styleInstruction}
${vocalPackInstruction}
${productionInstruction}
${bandIdentityInstruction}
${riffDnaInstruction}
${soloInstruction}
${narrativeInstruction}
[SPECIAL INSTRUCTIONS FOR PHYSICS LAYERS]
You MUST incorporate all provided physics layers into the LYRICS block:
- {LAYER-1 VOCAL PHYSICS} MUST be at the absolute top of the lyrics.
- {LAYER-2 RHYTHM SECTION PHYSICS} should be placed before the first verse or intro.
- {LAYER-3 GUITAR PHYSICS} should be placed before the first solo or heavy instrumental section.
- {LAYER-4 ATMOSPHERE PHYSICS} and {LAYER-5 SPECIALTY PHYSICS} (if provided) should be placed before sections where they become prominent.
- {LAYER-6 SOLO CHOREOGRAPHY} (if provided) MUST be used for the instrumental solo section.

MOOD: ${data.mood.join(", ")}
EXPLICIT: ${data.explicit}
POV/SETTING: ${data.povSetting}
THEMES: ${data.themesToHit}
AVOID: ${data.themesToAvoid}
INSTRUMENTS: ${data.instruments}
CONSTRAINTS: ${data.constraints}
STYLE_STRENGTH_TARGET: ${data.styleStrength}
WEIRDNESS_TARGET: ${data.weirdness}${isPresetOverride ? `\n\n[STYLE-TAGS REMINDER: The MOOD / THEMES / INSTRUMENTS / CONSTRAINTS above inform LYRICS and NARRATIVE only. The style/tags output derives EXCLUSIVELY from the Style Preset at the top of this prompt.]` : ''}`;

  const sections: PromptPreviewSection[] = [
    { label: 'Seed', content: data.seed },
    { label: 'Style', content: styleInstruction },
  ];

  if (vocalPackInstruction) sections.push({ label: isPersonaActive ? 'Vocal Architecture (Persona)' : 'Vocal Architecture', content: vocalPackInstruction.trim() });
  if (productionInstruction) sections.push({ label: isPersonaActive ? 'Production Architecture (Persona)' : 'Production Architecture', content: productionInstruction.trim() });
  if (bandIdentityInstruction) sections.push({ label: 'Band Identity', content: bandIdentityInstruction.trim() });
  if (riffDnaInstruction) sections.push({ label: 'Riff DNA', content: riffDnaInstruction.trim() });
  if (soloInstruction) sections.push({ label: 'Solo Choreography', content: soloInstruction.trim() });
  if (narrativeInstruction) sections.push({ label: 'Narrative Engine', content: narrativeInstruction.trim() });
  if (data.mood.length > 0) sections.push({ label: 'Mood', content: data.mood.join(', ') });
  if (data.povSetting) sections.push({ label: 'POV & Setting', content: data.povSetting });
  if (data.themesToHit) sections.push({ label: 'Themes', content: data.themesToHit });
  if (data.themesToAvoid) sections.push({ label: 'Avoid', content: data.themesToAvoid });
  if (data.instruments || data.constraints) {
    sections.push({ label: 'Constraints', content: [data.instruments, data.constraints].filter(Boolean).join(' | ') });
  }
  sections.push({ label: 'Sliders', content: `Style Strength: ${data.styleStrength}% | Weirdness: ${data.weirdness}%` });

  return {
    userPrompt,
    sections,
    charCount: userPrompt.length
  };
}
